<template>
  <aside-container>
    <div
      v-if="location"
      class="view-spacing"
    >
      <section>
        <LocationHero :id="id"/>

        <WeatherWidget
          v-if="location.weather"
          v-bind="location.weather"
          :header="location.title"
        />
      </section>

      <LiveLeaderboard
        v-if="
          [$options.modes.LIVE].includes(mode)
            && runners
            && hasFeature('liveLeaderboard')
            && eventLeaderboard
        "
        :runners="runners"
      />

      <ParticipantLeaderboard
        v-if="[$options.modes.POST].includes(mode)"
        :header="$t('welcome.globalWinners')"
        :value="globalLeader"
      />

      <div class="card">
        <div
          class="card-body text-center text-center smaller text-condensed text-uppercase font-weight-bold flex-wrap"
        >
          <h5 v-text="$t('location.info')"/>
          <div>
            <fact-item
              :title="$tc('location.participants.count', 99, [''])"
            >
              {{ location.runners.total.total }}
            </fact-item>
          </div>
          <a
            v-if="location.link"
            :href="location.link"
            target="_blank"
            class="btn btn-outline-secondary mt-3 w-100"
          >
            {{ $t('location.btnAbout') }}
          </a>
        </div>
      </div>
    </div>
  </aside-container>
</template>

<script>
import { mapGetters } from 'vuex'
import { characteristicIcons } from '@/constants/location'
import FactItem from '@/components/location/FactItem.vue'
import * as modes from '@/constants/modes'
import LocationHero from '@/components/location/LocationHero.vue'
import WeatherWidget from '@/components/WeatherWidget.vue'
import ParticipantLeaderboard from '@/components/cards/ParticipantLeaderboard.vue'
import LiveLeaderboard from '@/components/LiveLeaderboard.vue'
import { Poller } from '@/Poller'
import { WATCH_UPDATE_INTERVAL } from '@/constants/intervals'
import transformParticipant from '@/worker/transformer/transformParticipant'
import { isEqual } from 'lodash-es'

export default {
  name: 'LocationDetail',
  modes,
  characteristicIcons,
  components: {
    LiveLeaderboard,
    WeatherWidget,
    LocationHero,
    FactItem,
    ParticipantLeaderboard
  },
  props: {
    id: {
      type: String,
      default: undefined
    }
  },
  data() {
    return {
      eventLeaderboard: null,
      poller: new Poller(async (locationId) => {
        try {
          const response = await fetch(
            `${process.env.BASE_URL}${process.env.VUE_APP_JSON_URL}leaders/${locationId}.json`
          )
          if (response.ok && response.headers.get('content-type')
            .match(/application\/json/)) {
            return response.json()
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('failed to poll event leaderboard', error)
        }

        return undefined
      }, WATCH_UPDATE_INTERVAL)
        .on('response', (eventLeaderboard) => {
          if (isEqual(this.eventLeaderboard, eventLeaderboard)) {
            return
          }
          this.eventLeaderboard = eventLeaderboard
        })
        .on('error', (e) => {
          throw e
        })
    }
  },
  computed: {
    ...mapGetters([
      'mode',
      'globalLeader',
      'hasFeature',
      'getFeature',
      'locationById'
    ]),
    location() {
      return this.locationById(this.id)
    },
    locationHasLiveLeaderboard() {
      const locationId = this.location?.id
      if (!locationId) return false

      const convertedLocationId = this.toNumber(locationId)
      const featuredLocations = this.getFeature('locationLeaderBoard') || []
      const externalNumberArray = featuredLocations.map(id => this.toNumber(id))

      return externalNumberArray.includes(convertedLocationId)
    },
    runners() {
      if (!this.eventLeaderboard) {
        return undefined
      }

      const male = this.eventLeaderboard.m.map(maleP => transformParticipant(maleP))
      const female = this.eventLeaderboard.w.map(femaleP => transformParticipant(femaleP))
      return [...male, ...female]
    }
  },
  watch: {
    location() {
      if (!this.locationHasLiveLeaderboard) {
        this.eventLeaderboard = null
        this.poller.pause()
      } else {
        this.poller.fetch(this.location.id)
      }
    },
    locationHasLiveLeaderboard() {
      this.poller.fetch(this.location.id)
    }
  },
  activated() {
    if (this.locationHasLiveLeaderboard) {
      this.poller.resume()
    }
  },
  deactivated() {
    this.poller.pause()
  },
  beforeDestroy() {
    this.poller.stop()
  },
  methods: {
    /**
     * @param stringOrNumber {number|string}
     */
    toNumber(stringOrNumber) {
      return typeof stringOrNumber === 'string' ? Number(stringOrNumber) : stringOrNumber
    }
  }
}
</script>
